import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Link } from '@tanstack/react-router';
import type React from 'react';
import { ACTION_LABELS, type EXCEPTION_ACTIONS, type ExceptionLog } from '../../data/exception-log';
import type { Product } from '../../data/product';
import { compare } from '../../helpers/column-sort';
import { formatPercent, fullCurrencyFormatter } from '../../helpers/format-currency';
import { formatInStoreTimezone } from '../../helpers/format-date';
import { Tooltip } from '../popover/tooltip';
import { ItemCode as ProductItemCode } from '../product-table/columns/item-code-column';
import { ProductNameWithLabels } from '../product-table/columns/product-name-with-labels-column';
import { Size as ProductSize } from '../product-table/columns/size-column';
import { defineColumn, remapColumn, type StrictColumnDef } from '../tanstack-table/column';

function SalesJournalLink({
  storeId,
  search,
  children,
}: {
  storeId: string;
  search: { from: string; to: string; receipt?: string };
  children: React.ReactNode;
}) {
  return (
    <Link
      to="/stores/$storeId/reports/sales-journal"
      params={{ storeId }}
      search={search}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary-600 hover:text-primary-800 hover:underline"
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </Link>
  );
}

function buildSalesJournalSearch(
  timestamp: Date,
  timezone: string,
  invoiceNumber?: number
): { from: string; to: string; receipt?: string } {
  const date = formatInStoreTimezone(timestamp, timezone, 'yyyy-MM-dd');

  return {
    from: date,
    to: date,
    ...(invoiceNumber != null && { receipt: String(invoiceNumber) }),
  };
}

export const __TEST_ONLY_buildSalesJournalSearch = buildSalesJournalSearch;

const LINKED_EXCEPTION_ACTIONS: Set<string> = new Set<(typeof EXCEPTION_ACTIONS)[number]>([
  'edit',
  'refund',
  'below_cost',
  'discount',
]);

export const CloseNum: StrictColumnDef<ExceptionLog> = defineColumn<ExceptionLog>({
  id: 'Close #',
  minWidth: 65,
  accessorFn: (log) => log.close_num,
});

export const Action: StrictColumnDef<ExceptionLog> = defineColumn<ExceptionLog>({
  id: 'Action',
  minWidth: 100,
  accessorFn: (log) => ACTION_LABELS[log.action] ?? log.action,
});

export const Initials: StrictColumnDef<ExceptionLog> = defineColumn<ExceptionLog>({
  id: 'Login / Emp.',
  minWidth: 80,
  accessorFn: (log) => log.initials,
});

export const Station: StrictColumnDef<ExceptionLog> = defineColumn<ExceptionLog>({
  id: 'Station',
  minWidth: 65,
  accessorFn: (log) => log.station,
});

export const Barcode: StrictColumnDef<ExceptionLog> = defineColumn<ExceptionLog>({
  id: 'Barcode',
  minWidth: 120,
  accessorFn: (log) => (log.barcode === 'DRAWER OPENED' ? '' : log.barcode),
});

export const Quantity: StrictColumnDef<ExceptionLog> = defineColumn<ExceptionLog>({
  id: 'Qty',
  minWidth: 40,
  accessorFn: (log) => (log.quantity ? String(log.quantity) : ''),
  sortingFn: (a, b) => a.quantity - b.quantity,
});

export const OldPrice: StrictColumnDef<ExceptionLog> = defineColumn<ExceptionLog>({
  id: 'Old Price',
  minWidth: 75,
  accessorFn: (log) => (log.old_price ? fullCurrencyFormatter(log.old_price) : ''),
  sortingFn: (a, b) => a.old_price - b.old_price,
  toCSV: (log) => ({ 'Old Price': log.old_price.toFixed(2) }),
});

export const NewPrice: StrictColumnDef<ExceptionLog> = defineColumn<ExceptionLog>({
  id: 'New Price',
  minWidth: 80,
  accessorFn: (log) => (log.new_price ? fullCurrencyFormatter(log.new_price) : ''),
  sortingFn: (a, b) => a.new_price - b.new_price,
  toCSV: (log) => ({ 'New Price': log.new_price.toFixed(2) }),
  cell: ({ item, value }) => {
    if (!item.old_price || !item.new_price) {
      return value;
    }

    const className =
      item.new_price < item.old_price
        ? 'text-red-600'
        : item.new_price > item.old_price
          ? 'text-green-600'
          : '';

    return <span className={className}>{value}</span>;
  },
});

export const ExtendedPrice: StrictColumnDef<ExceptionLog> = defineColumn<ExceptionLog>({
  id: 'Extended Price',
  minWidth: 80,
  accessorFn: (log) => {
    if (!log.quantity || !log.new_price) {
      return '';
    }

    return fullCurrencyFormatter(log.quantity * log.new_price);
  },
  sortingFn: (a, b) => a.quantity * a.new_price - b.quantity * b.new_price,
  toCSV: (log) => ({
    'Extended Price': (log.quantity * log.new_price).toFixed(2),
  }),
});

export type ProductLookup = (log: ExceptionLog) => Product | undefined;

export function buildExceptionLogColumns(
  findProduct: ProductLookup,
  timezone: string,
  storeId: string
): StrictColumnDef<ExceptionLog>[] {
  // These carry their source column's cell and toCSV through, so the Product
  // column renders its labels and splits Brand/Description in the CSV export
  // here exactly as it does in every other table.
  const Product = remapColumn(ProductNameWithLabels, findProduct);
  const SizeCol = remapColumn(ProductSize, findProduct);
  const ItemCode = remapColumn(ProductItemCode, findProduct);

  const InvoiceNum: StrictColumnDef<ExceptionLog> = {
    ...defineColumn<ExceptionLog>({
      id: 'Inv. #',
      minWidth: 75,
      accessorFn: (log) => (log.invoice_number ? String(log.invoice_number) : ''),
      sortingFn: (a, b) => a.invoice_number - b.invoice_number,
      cell: ({ item, value }) => {
        if (!item.invoice_number) {
          return value;
        }

        return (
          <SalesJournalLink
            storeId={storeId}
            search={buildSalesJournalSearch(item.timestamp, timezone, item.invoice_number)}
          >
            {value}
          </SalesJournalLink>
        );
      },
    }),
    header: () => (
      <span className="inline-flex items-center gap-1 whitespace-nowrap">
        Inv. #
        <Tooltip content="Best-effort match based on codenum / barcode and timestamp" side="bottom">
          <span>
            <InformationCircleIcon className="h-3.5 w-3.5 text-gray-400 hover:text-gray-500" />
          </span>
        </Tooltip>
      </span>
    ),
  };

  const Timestamp: StrictColumnDef<ExceptionLog> = defineColumn<ExceptionLog>({
    id: 'Timestamp',
    minWidth: 185,
    accessorFn: (log) => formatInStoreTimezone(log.timestamp, timezone, 'MM/dd/yyyy, hh:mm:ss a'),
    sortingFn: (a, b) => compare(a.timestamp, b.timestamp),
    cell: ({ item, value }) => {
      if (item.invoice_number || !LINKED_EXCEPTION_ACTIONS.has(item.action)) {
        return value;
      }

      return (
        <SalesJournalLink
          storeId={storeId}
          search={buildSalesJournalSearch(item.timestamp, timezone)}
        >
          {value}
        </SalesJournalLink>
      );
    },
  });

  const LastCost: StrictColumnDef<ExceptionLog> = defineColumn<ExceptionLog>({
    id: 'Last Cost',
    minWidth: 75,
    accessorFn: (log) => {
      const product = findProduct(log);
      if (!product) {
        return '';
      }

      return fullCurrencyFormatter(product.last_cost);
    },
    sortingFn: (a, b) => {
      const costA = findProduct(a)?.last_cost ?? 0;
      const costB = findProduct(b)?.last_cost ?? 0;

      return costA - costB;
    },
    toCSV: (log) => ({
      'Last Cost': (findProduct(log)?.last_cost ?? 0).toFixed(2),
    }),
  });

  const Margin: StrictColumnDef<ExceptionLog> = defineColumn<ExceptionLog>({
    id: 'Margin %',
    minWidth: 70,
    accessorFn: (log) => {
      if (!log.new_price) {
        return '';
      }

      const product = findProduct(log);
      if (!product) {
        return '';
      }

      const margin = (log.new_price - product.last_cost) / log.new_price;

      return formatPercent(margin);
    },
    sortingFn: (a, b) => {
      const costA = findProduct(a)?.last_cost ?? 0;
      const costB = findProduct(b)?.last_cost ?? 0;
      const marginA = a.new_price ? (a.new_price - costA) / a.new_price : 0;
      const marginB = b.new_price ? (b.new_price - costB) / b.new_price : 0;

      return marginA - marginB;
    },
  });

  return [
    CloseNum,
    InvoiceNum,
    Timestamp,
    Action,
    Initials,
    Station,
    ItemCode,
    Barcode,
    Product,
    SizeCol,
    Quantity,
    LastCost,
    OldPrice,
    NewPrice,
    ExtendedPrice,
    Margin,
  ];
}
